import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://autodistrict.be';
const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
const BRAND = 'AutoDistrict';
const PHONE = '+32489432161';
const EMAIL = 'info@autodistrict.be';
const ADDRESS = {
  locality: 'Wavre',
  country: 'BE',
};

const SUPPORTED_LANGS = ['fr', 'en', 'nl'];

/**
 * Page-level SEO component.
 * Renders:
 *   - <title>, meta description, keywords, og:*, twitter:*, robots
 *   - canonical + hreflang (3 languages)
 *   - JSON-LD AutomotiveBusiness (sitewide)
 *   - Optional JSON-LD Vehicle / BreadcrumbList passed in via `extraJsonLd`
 */
const Seo = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  noIndex = false,
  extraJsonLd = null,
}) => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const lang = (SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : 'fr');

  const canonical = `${SITE_URL}${pathname}`;
  const fullTitle = title.includes(BRAND) ? title : `${title} | ${BRAND}`;

  const localBusinessJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AutomotiveBusiness',
    name: BRAND,
    legalName: 'SHLI SRL',
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    image: `${SITE_URL}/logo512.png`,
    telephone: PHONE,
    email: EMAIL,
    address: {
      '@type': 'PostalAddress',
      addressLocality: ADDRESS.locality,
      addressCountry: ADDRESS.country,
    },
    areaServed: { '@type': 'Country', name: 'Belgium' },
    priceRange: '€€-€€€',
    slogan: 'Driven by Quality',
    knowsAbout: ['Used cars', 'Pre-owned vehicles', 'Vehicle sales', 'Trade-in', 'Auto financing'],
    paymentAccepted: ['Cash', 'Bank transfer', 'Financing'],
    sameAs: [],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
        description: 'By appointment only',
      },
    ],
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noIndex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} />
      <link rel="canonical" href={canonical} />

      {/* hreflang */}
      {SUPPORTED_LANGS.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE_URL}${pathname}${pathname.includes('?') ? '&' : '?'}lang=${l}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${pathname}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang === 'nl' ? 'nl_BE' : 'fr_BE'} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD: site-wide AutomotiveBusiness */}
      <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>

      {/* Optional extra JSON-LD (e.g. Vehicle / BreadcrumbList) */}
      {extraJsonLd && (
        <script type="application/ld+json">{JSON.stringify(extraJsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
